import { useId } from 'react'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Select } from '~/components/ui/select'
import { cn } from '~/lib/utils'

// 카탈로그 항목 편집기에서 공유하는 폼 컨트롤. 동작·외형 변경 없이 추출했다.

// 컨트롤에 주입되는 접근성 props. id는 <Label htmlFor>와 연결되고,
// error가 있으면 aria-describedby로 에러 텍스트를 컨트롤에 묶는다.
export interface FieldControlProps {
    id: string
    'aria-describedby'?: string
    'aria-invalid'?: boolean
}

export function Field({
    label,
    required,
    dirty,
    error,
    children,
}: {
    label: string
    required?: boolean
    dirty?: boolean
    error?: string
    // useId로 생성한 id·aria-* 를 받아 컨트롤에 펼쳐 넣는다(Label·control 공유).
    children: (control: FieldControlProps) => React.ReactNode
}) {
    const id = useId()
    const errorId = `${id}-error`
    const control: FieldControlProps = {
        id,
        'aria-describedby': error ? errorId : undefined,
        'aria-invalid': error ? true : undefined,
    }
    return (
        <div>
            <Label htmlFor={id} className='flex items-center gap-1'>
                {label}
                {required ? <span className='text-destructive'>*</span> : null}
                {dirty ? (
                    <span className='text-primary text-xs font-bold'>
                        · 변경됨
                    </span>
                ) : null}
            </Label>
            <div className='mt-1'>{children(control)}</div>
            {error ? (
                <p id={errorId} className='text-destructive mt-1 text-xs'>
                    {error}
                </p>
            ) : null}
        </div>
    )
}

export function InputField({
    value,
    dirty,
    onChange,
    ...control
}: {
    value: string
    dirty?: boolean
    onChange: (v: string) => void
} & FieldControlProps) {
    return (
        <Input
            {...control}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={cn(dirty && 'border-primary ring-primary/30 ring-2')}
        />
    )
}

export function SelectField({
    options,
    value,
    dirty,
    onChange,
    ...control
}: {
    options: { value: string; label: string }[]
    value: string
    dirty?: boolean
    onChange: (v: string) => void
} & FieldControlProps) {
    return (
        <Select
            {...control}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={cn(dirty && 'border-primary ring-primary/30 ring-2')}>
            {options.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </Select>
    )
}

export function Textarea({
    value,
    rows,
    dirty,
    onChange,
    ...control
}: {
    value: string
    rows: number
    dirty?: boolean
    onChange: (v: string) => void
} & FieldControlProps) {
    return (
        <textarea
            {...control}
            rows={rows}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={cn(
                'border-input bg-card text-foreground w-full rounded-[var(--radius)] border px-3 py-2 text-sm transition-colors',
                'placeholder:text-muted-foreground',
                'focus:border-primary focus:ring-ring/30 focus:ring-2 focus:outline-none',
                dirty && 'border-primary ring-primary/30 ring-2'
            )}
        />
    )
}
